import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Leerkr8PlaythroughRequest, Leerkr8PlaythroughResponse } from '@core/saves/leerkr8/types';
import { getDefaultSaveState, ISaveState } from '@core/saves/saves.interface';
import { environment } from '@environments/environment';
import { lastValueFrom } from 'rxjs';
import { ILeerkr8ApiService } from './leerkr8-api.service.interface';

@Injectable({
  providedIn: 'root',
})
export class Leerkr8ApiService implements ILeerkr8ApiService {
  private http = inject(HttpClient);

  private cachedGameSave?: {
    date: number;
    state: ISaveState;
  };

  startSession(playerCode: string) {
    delete this.cachedGameSave;

    return this.http.post<{
      sessionId: string;
      playerCode: string;
      localeId: string;
      properties: Record<string, string>; // not really clear what is in this
    }>(this.buildUrl('rest/cloud/startsession'), {
      playerCode,
      apiKey: environment.saveService.leerkr8.api_key,
      gameRef: 'woordkr8',
    });
  }

  async getGameSave(sessionId: string, playerCode: string): Promise<ISaveState> {
    if (this.cachedGameSave && this.cachedGameSave.date > Date.now() - 60 * 60_000) {
      return this.cachedGameSave.state;
    }

    const request = this.http.get<{
      gameRef: string;
      preferences: Record<string, string>;
      progress: Record<string, string>;
    }>(this.buildUrl('rest/cloud/game/woordkr8/save'), {
      headers: {
        'X-Api-Key': environment.saveService.leerkr8.api_key,
        'X-Session-Id': sessionId,
        'X-Player-Code': playerCode,
      },
    });

    const data = await lastValueFrom(request);
    const state = Object.fromEntries(Object.entries(data.progress).map(([key, value]) => [key, JSON.parse(value)])) as Partial<ISaveState>;

    return { ...getDefaultSaveState(), ...state } satisfies ISaveState;
  }

  setGameSave(sessionId: string, playerCode: string, save: ISaveState) {
    this.cachedGameSave = {
      date: Date.now(),
      state: save,
    };

    return this.http.post(
      this.buildUrl('rest/cloud/game/woordkr8/save'),
      {
        progress: Object.fromEntries(Object.entries(save).map(([key, value]) => [key, JSON.stringify(value)])),
        preferences: {},
      },
      {
        headers: {
          'X-Api-Key': environment.saveService.leerkr8.api_key,
          'X-Session-Id': sessionId,
          'X-Player-Code': playerCode,
        },
      },
    );
  }

  activateDevice(activationCode: string, deviceIdentifier: string, deviceName: string) {
    return this.http.post<{
      instituteName: string;
      token: string;
    }>(this.buildUrl('rest/cloud/activateInstituteDevice'), {
      apiKey: environment.saveService.leerkr8.api_key,
      code: activationCode,
      deviceIdentifier,
      deviceName,
    });
  }

  getPlayerGroup(deviceToken: string, deviceIdentifier: string, playerGroupCode: string) {
    return this.http.post<{
      playerCodes: string[];
      playerSaves: {
        properties: Record<string, string>;
        nickname: string;
        fullName: string;
        localeId: string;
      }[];
      skipParentalGuard: boolean;
    }>(this.buildUrl('rest/cloud/getPlayerGroup'), {
      apiKey: environment.saveService.leerkr8.api_key,
      deviceToken,
      deviceIdentifier,
      playerGroupCode,
    });
  }

  postPlaythrough(sessionId: string, playerCode: string, playthrough: Leerkr8PlaythroughRequest) {
    return this.http.post<Leerkr8PlaythroughResponse>(this.buildUrl('rest/cloud/game/woordkr8/playthrough'), playthrough, {
      headers: {
        'X-Api-Key': environment.saveService.leerkr8.api_key,
        'X-Session-Id': sessionId,
        'X-Player-Code': playerCode,
      },
    });
  }

  private buildUrl(endpoint: string): string {
    return `${environment.saveService.leerkr8.url}${endpoint}`;
  }
}
